import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Trash2, Eye, FileText } from "lucide-react";
import { toast } from "sonner";
import { API_BASE_URL } from "../config/api";

import { useAuth } from "@/config/auth";
import { localDrafts, forkAssessment } from "@/lib/assessmentDrafts";
const API = API_BASE_URL;

const getScoreColor = (score) => {
  if (score >= 7) return "text-[#388E3C]";
  if (score >= 4) return "text-[#F57F17]";
  return "text-[#D32F2F]";
};

export default function AssessmentList() {
  const navigate = useNavigate();
  const {user} = useAuth();
  const [drafts,setDrafts]=useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAssessments(); }, []);

  const loadAssessments = async () => {
    try {
      const res = await axios.get(`${API}/assessments`);
      setAssessments(res.data);
      let remote=[];
      try { remote=(await axios.get(`${API}/drafts`)).data; } catch { toast.error('Draft sync unavailable; showing device drafts'); }
      const merged=new Map(remote.map(d=>[d.id,d]));
      for(const d of localDrafts(user.id)) if(!merged.has(d.id) || merged.get(d.id).updated_at<d.updated_at) merged.set(d.id,d);
      setDrafts([...merged.values()].sort((a,b)=>b.updated_at.localeCompare(a.updated_at)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fork = async (id, revision) => {
    try { const draft=await forkAssessment(id,revision,user.id); navigate(`/advanced/new?draft=${draft}`); }
    catch { toast.error('Could not create draft. Please retry.'); }
  };
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/assessments/${id}`);
      toast.success("Assessment deleted");
      loadAssessments();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#43A047]" /></div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="assessment-list">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl tracking-tight font-light text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>
            Assessments
          </h1>
          <p className="text-sm text-[#617364] mt-1">{assessments.length} assessments</p>
        </div>
        <Button onClick={() => navigate("/advanced/new")} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="new-assessment-btn">
          New Assessment
        </Button>
      </div>

      {drafts.length > 0 && <Card className="mb-6"><CardHeader><CardTitle>Drafts</CardTitle></CardHeader><CardContent>{drafts.map(d=><div key={d.id} className="flex items-center justify-between border-b py-3"><div><strong>{d.payload?.form?.product_name || 'Untitled product'}</strong><p className="text-xs">Saved {new Date(d.updated_at).toLocaleString()} · Incomplete work is kept</p></div><Button variant="outline" onClick={()=>navigate(`/advanced/new?draft=${d.id}`)}>Continue draft</Button></div>)}</CardContent></Card>}
      {assessments.length === 0 ? (
        <Card className="border-[#E0E6E1] bg-white">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-[#E0E6E1] mx-auto mb-4" />
            <p className="text-[#617364]">No assessments yet. Create your first one.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E0E6E1] bg-white">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E0E6E1]">
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Product</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">TOSS Score / 100</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assessments.map(a => (
                  <TableRow key={a.id} className="border-[#E0E6E1] hover:bg-[#F8F9F8]">
                    <TableCell className="text-sm text-[#1B291D] font-medium">{a.product_name}{a.revision_of && <Badge variant="outline" className="ml-2">Revision</Badge>}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-[#E0E6E1] text-[#617364] text-xs">{a.product_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {a.results?.upsi != null ? (
                        <span className={`text-lg font-medium ${getScoreColor(a.results.upsi)}`} style={{ fontFamily: 'Work Sans' }}>
                          {(a.results.upsi * 10).toFixed(2)}
                        </span>
                      ) : "N/A"}<small className="block text-xs text-slate-500">{a.results?.aggregation?.method === "process_b" ? "Process B" : "Earlier method"}</small>
                    </TableCell>
                    <TableCell className="text-sm text-[#617364]">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/advanced/assessments/${a.id}`)} className="text-[#43A047]" data-testid={`view-${a.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={()=>fork(a.id,false)}>Duplicate assessment</Button>
                        <Button variant="ghost" size="sm" onClick={()=>fork(a.id,true)}>Create revised version</Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id)} className="text-[#D32F2F]" data-testid={`delete-${a.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
